import React, { useState, useEffect, useCallback, useRef } from 'react';
import {
  Box,
  Typography,
  Card,
  Button,
  FormControl,
  FormLabel,
  Input,
  Textarea,
  Select,
  Option,
  Chip,
  CircularProgress,
  Alert,
  Grid,
  Stack,
  Divider,
  Switch,
  FormHelperText,
  Modal,
  ModalDialog,
  Autocomplete,
  AutocompleteRenderOptionState,
  CardContent,
  ListItemProps,
  IconButton,
  DialogTitle,
  DialogContent,
  DialogActions
} from '@mui/joy';
import { useParams, useNavigate, useBeforeUnload } from 'react-router-dom';
import { FaArrowLeft, FaSave, FaTrash, FaPlus } from 'react-icons/fa';
import { useProject, createProject, deleteProject } from '../hooks/useProjects';
import type { Project } from '../types/project';
import { useCustomerStore } from '../store/customerStore';
import { useCustomer } from '../hooks/useCustomer';
import { directSupabase } from '../lib/supabaseClient';
import {
  emitProjectCreatedActivity,
  withProjectCreator,
} from '../services/activityEvents.service';
import type { ColorPaletteProp } from '@mui/joy';
import ProjectWorkflow, { WorkflowTask, PROGRESS_STAGES, WORKFLOW_STAGES, WorkflowStage, getStageFromTasks, SUPPLY_ONLY_WORKFLOW_STAGES, SUPPLY_ONLY_PROGRESS_STAGES } from '../components/ProjectWorkflow';
import { sendTemplateEmail } from '../utils/graphService';
import { getFullWorkflowTasks, getFullWorkflowTasksForMode } from '../utils/workflowUtils';
import { IconWrapper } from '../components/IconWrapper';
import CustomerCreationForm from '../components/CustomerCreationForm';
import type { Customer } from '../services/customerService';
import { assertJobRecordAllowsComplete } from '../services/jobReviews.service';
import type { WorkflowTask as WorkflowTaskType } from '../types/workflow';
import type { ProjectFormData } from '../types/project';
import { PRODUCT_TYPE_OPTIONS } from './project-workspace/WorkspaceProductTypesCard';
import { mintWorkspaceLifecycleRef } from './project-workspace/workspaceLifecycleRef';
import { brandForCustomer, saveWorkspaceBrand } from './project-workspace/workspaceBrand';
import { organisationSettings } from '../services/organisationSettings';
import { defaultUseBillingDetailsForDocuments } from './project-workspace/workspaceBillingIdentity';


// Initial form data
const initialFormData: ProjectFormData = {
  project_name: '',
  customer_id: '',
  customer_name: '',
  customer_email: '',
  notes: '',
  deposit_received: false,
  ordered: false,
  delivery_date: null,
  install_date: null,
  product_types: [],
  supplier_quote_received: false,
  supplier_prices: [],
  complete: false,
  project_status: 'active',
  workflow_tasks: [],
  preferred_suppliers: [],
  onedrive_folder_id: null,
  supply_only: false,
};

// Helper function to determine progress stage
function getProgressStage(formData: ProjectFormData): { label: string; color: 'success' | 'danger' | 'warning' | 'primary' | 'neutral' } {
  // Determine stage based on boolean flags and/or workflow_tasks
  if (formData.complete) return { label: 'Completed', color: 'success' };
  if (formData.deposit_received) return { label: 'Deposit Received', color: 'warning' };
  if (formData.supplier_quote_received) return { label: 'Supplier Quote Received', color: 'primary' };
  return { label: 'New', color: 'neutral' };
}

// Helper to normalize a Project to ProjectFormData
function normalizeProjectToFormData(project: Project): ProjectFormData {
  return {
    id: project.id,
    project_name: project.project_name || '',
    customer_id: project.customer_id?.toString() || '',
    customer_name: project.customer_name || undefined,
    customer_email: project.customer_email || undefined,
    notes: project.notes || '',
    deposit_received: project.deposit_received || false,
    ordered: project.ordered || false,
    delivery_date: project.delivery_date || null,
    install_date: project.install_date || null,
    product_types: Array.isArray(project.product_types) ? project.product_types : [],
    supplier_quote_received: project.supplier_quote_received || false,
    supplier_prices: project.supplier_prices || [],
    complete: project.complete ?? false,
    project_status: project.project_status || 'active',
    workflow_tasks: project.workflow_tasks || [],
    preferred_suppliers: project.preferred_suppliers || [],
    onedrive_folder_id: project.onedrive_folder_id || null,
    supply_only: project.supply_only || false,
  };
}

// Helper to convert project data to form data
function convertProjectToFormData(project: Project): ProjectFormData {
  return {
    id: project.id,
    project_name: project.project_name || '',
    customer_id: project.customer_id?.toString() || '',
    customer_name: project.customer_name || undefined,
    customer_email: project.customer_email || undefined,
    notes: project.notes || '',
    deposit_received: project.deposit_received || false,
    ordered: project.ordered || false,
    delivery_date: project.delivery_date || null,
    install_date: project.install_date || null,
    product_types: project.product_types || [],
    supplier_quote_received: project.supplier_quote_received || false,
    supplier_prices: project.supplier_prices || [],
    complete: project.complete || false,
    project_status: project.project_status || 'active',
    workflow_tasks: project.workflow_tasks || [],
    preferred_suppliers: project.preferred_suppliers || [],
    onedrive_folder_id: project.onedrive_folder_id || null,
    supply_only: project.supply_only || false,
  };
}

// Helper to get initial workflow tasks for a project
function getInitialWorkflowTasks() {
  // Flatten all tasks from all stages into a single array
  return Object.values(WORKFLOW_STAGES).flat().map(task => ({ 
    task, 
    completed: false,
    productionDates: undefined
  }));
}

// Add helper function for supplier price date validation
const validateSupplierPriceDate = (date: string | null | undefined): boolean => {
  if (!date) return true; // Allow empty dates
  const inputDate = new Date(date);
  const today = new Date();
  
  // Allow any past date, but prevent future dates
  return inputDate <= today;
};

// Utility to get all workflow tasks for the selected workflow mode
// (Removed local getFullWorkflowTasksForMode function, now using imported version)

// Compatibility function to convert unified Customer to old CustomerContext type
const convertCustomerForEmail = (customer: Customer) => ({
  id: parseInt(customer.id, 10),
  name: customer.name,
  first_name: customer.name.split(' ')[0] || '',
  last_name: customer.name.split(' ').slice(1).join(' ') || '',
  phone_number: customer.phone,
  email: customer.email,
  address: customer.address,
  billing_address: customer.notes || null,
  organization_id: customer.reference || '1',
  organization_name: customer.company || '',
  created_at: customer.created_at,
  updated_at: customer.updated_at
});

const ProjectFormPage: React.FC = () => {
  const { projectId } = useParams<{ projectId: string }>();
  const navigate = useNavigate();
  const { project, loading: projectLoading, error: projectError, updateProject, refetch } = useProject(projectId);
  const { customers, loading: customersLoading, loadCustomers } = useCustomerStore();
  
  // Form state
  const [formData, setFormData] = useState<ProjectFormData>(initialFormData);
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);
  
  // Add state for suppliers
  const [allSuppliers, setAllSuppliers] = useState<any[]>([]);
  const [supplierPrices, setSupplierPrices] = useState<{
    supplier_id: string;
    supplier_name: string;
    price: string | null;
    supplier_price_date_obtained?: string | null;
    quote_reference?: string | null;
  }[]>([]);
  
  // Add state for workflow tasks
  const [workflowTasks, setWorkflowTasks] = useState<WorkflowTask[]>(getInitialWorkflowTasks());
  
  // Add state for delete dialog
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  
  // Add isDirty state
  const [isDirty, setIsDirty] = useState(false);

  // Track if initial data has been loaded
  const [initialDataLoaded, setInitialDataLoaded] = useState(false);
  
  // Add save state tracking
  const [isSaving, setIsSaving] = useState(false);
  const [lastSaveError, setLastSaveError] = useState<string | null>(null);

  // Track last saved time
  const [lastSaved, setLastSaved] = useState<Date | null>(null);
  
  // Add state for missing customer
  const [missingCustomer, setMissingCustomer] = useState<Customer | null>(null);
  
  // Add state for navigation guard
  const [showNavigationModal, setShowNavigationModal] = useState(false);
  const [pendingNavigation, setPendingNavigation] = useState<string | null>(null);

  // Add state for preferred suppliers
  const [preferredSuppliers, setPreferredSuppliers] = useState<string[]>([]);
  
  // Add a new state to track if we're in the middle of loading/syncing data
  const [isSyncingData, setIsSyncingData] = useState(true);
  
  // Add ref to track if we're navigating after a save (to bypass navigation guards)
  const isNavigatingAfterSaveRef = useRef(false);

  // Add ref to track if customer lookup has been done
  const customerLookupDoneRef = useRef(false);

  // Add state for real-time customer lookup
  const [realTimeCustomer, setRealTimeCustomer] = useState<Customer | null>(null);
  const [customerLookupLoading, setCustomerLookupLoading] = useState(false);

  // Add navigation guard
  useBeforeUnload(
    useCallback(
      (event) => {
        if (isDirty && projectId && !isNavigatingAfterSaveRef.current) {
          event.preventDefault();
          event.returnValue = 'You have unsaved changes. Are you sure you want to leave?';
          return 'You have unsaved changes. Are you sure you want to leave?';
        }
      },
      [isDirty, projectId]
    )
  );

  // Navigation guard function
  const handleNavigationAttempt = useCallback((targetPath: string) => {
    if (isDirty && projectId) {
      setPendingNavigation(targetPath);
      setShowNavigationModal(true);
    } else {
      navigate(targetPath);
    }
  }, [isDirty, projectId, navigate]);

  // Handle navigation confirmation
  const handleNavigationConfirm = useCallback(async () => {
    if (pendingNavigation) {
      setShowNavigationModal(false);
      setPendingNavigation(null);
      navigate(pendingNavigation);
    }
  }, [pendingNavigation, navigate]);

  // Handle navigation cancellation
  const handleNavigationCancel = useCallback(() => {
    setShowNavigationModal(false);
    setPendingNavigation(null);
  }, []);

  // Handle customer selection
  const handleCustomerSelect = (customer: Customer | null) => {
    if (customer) {
      setFormData(prev => ({
        ...prev,
        customer_id: customer.id,
        customer_name: customer.name,
        customer_email: customer.email || ''
      }));
    } else {
      setFormData(prev => ({
        ...prev,
        customer_id: '',
        customer_name: '',
        customer_email: ''
      }));
    }
  };

  // Function to do real-time customer lookup from external API
  const lookupCustomerRealTime = useCallback(async (customerId: string | number) => {
    if (!customerId) return null;
    
    setCustomerLookupLoading(true);
    try {
      // Fetch fresh customer data from external API
      const { data, error } = await directSupabase
        .from('customers')
        .select('*')
        .order('name', { ascending: true });
      
      if (error) {
        throw new Error(error.message);
      }
      
      if (data) {
        const customerData = data.find((c: any) => c.id === customerId.toString());
        if (customerData) {
          // Convert to unified Customer format
          const customer: Customer = {
            id: customerData.id?.toString() || '',
            name: customerData.name,
            email: customerData.email,
            phone: customerData.phone_number,
            address: customerData.address,
            company: customerData.organization_name || undefined,
            created_at: customerData.created_at,
            updated_at: customerData.updated_at,
            source: 'local' as const
          };
          setRealTimeCustomer(customer);
          return customer;
        }
      }
      setRealTimeCustomer(null);
      return null;
    } catch (error) {
      console.error('Error in real-time customer lookup:', error);
      setRealTimeCustomer(null);
      return null;
    } finally {
      setCustomerLookupLoading(false);
    }
  }, []);

  // Fetch suppliers on mount
  useEffect(() => {
    const fetchSuppliers = async () => {
      try {
        const { data, error } = await directSupabase
          .from('suppliers')
          .select('*')
          .order('supplier_name', { ascending: true });
        
        if (error) {
          throw new Error(error.message);
        }
        
        if (data) {
          setAllSuppliers(data);
        }
      } catch (error) {
        console.error('Failed to fetch suppliers:', error);
      }
    };
    fetchSuppliers();
  }, []);
  
  // Modify the initial data loading effect
  useEffect(() => {
    if (!initialDataLoaded && !projectLoading && !customersLoading) {
      setIsSyncingData(true);  // Start syncing
      if (projectId && project) {
  
        const convertedFormData = convertProjectToFormData(project);
        setFormData(convertedFormData);
        setSupplierPrices(project.supplier_prices || []);
        setWorkflowTasks(project.workflow_tasks || []);
        setPreferredSuppliers(project.preferred_suppliers || []);
        
        setInitialDataLoaded(true);
      } else if (!projectId) {
        // For new projects, just mark as loaded
        setInitialDataLoaded(true);
        setPreferredSuppliers([]);
      }
      setIsSyncingData(false);  // Done syncing
    }
  }, [projectId, project, projectLoading, customersLoading, initialDataLoaded]);

  // Reset customer lookup ref when project changes
  useEffect(() => {
    customerLookupDoneRef.current = false;
  }, [projectId]);

  // Separate effect to handle customer lookup after data is loaded
  useEffect(() => {
    if (initialDataLoaded && projectId && project && project.customer_id && !customerLookupDoneRef.current) {
      customerLookupDoneRef.current = true; // Mark as done to prevent re-runs
      
      // Do real-time customer lookup instead of using cached data
      lookupCustomerRealTime(project.customer_id).then((customer) => {
        if (customer) {
          handleCustomerSelect(customer);
        } else {
          // Fallback to project data if customer not found
          setFormData(prev => ({
            ...prev,
            customer_id: project.customer_id?.toString() || '',
            customer_name: project.customer_name || '',
            customer_email: project.customer_email || ''
          }));
        }
      });
    }
  }, [initialDataLoaded, projectId, project, lookupCustomerRealTime]);

  // Modify the supplier prices sync effect to not trigger auto-save
  useEffect(() => {
    if (initialDataLoaded && !isSyncingData) {
      const pricesChanged = JSON.stringify(formData.supplier_prices) !== JSON.stringify(supplierPrices);
      if (pricesChanged) {
        setIsSyncingData(true);
        setFormData(prev => ({
          ...prev,
          supplier_prices: supplierPrices
        }));
        setIsSyncingData(false);
      }
    }
  }, [supplierPrices, initialDataLoaded, isSyncingData]);

  // Modify the preferred suppliers sync effect to not trigger auto-save
  useEffect(() => {
    if (initialDataLoaded && !isSyncingData) {
      const preferredChanged = JSON.stringify(formData.preferred_suppliers) !== JSON.stringify(preferredSuppliers);
      if (preferredChanged) {
        setIsSyncingData(true);
        setFormData(prev => ({
          ...prev,
          preferred_suppliers: preferredSuppliers
        }));
        setIsSyncingData(false);
      }
    }
  }, [preferredSuppliers, initialDataLoaded, isSyncingData]);

  // Disabled this effect - it was causing state to be reset after user changes
  // The initial data loading effect (line 357) handles initial load
  // useEffect(() => {
  //   if (project && !isSyncingData && initialDataLoaded && projectId) {
  //     const projectTasks = project.workflow_tasks || [];
  //     if (JSON.stringify(workflowTasks) !== JSON.stringify(projectTasks)) {
  //       setIsSyncingData(true);
  //       setWorkflowTasks(projectTasks);
  //       setIsSyncingData(false);
  //     }
  //   }
  //   // eslint-disable-next-line react-hooks/exhaustive-deps
  // }, [project?.workflow_tasks, project, isSyncingData, initialDataLoaded, projectId]);

  // Modify handleChange to set isDirty only when not syncing
  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
    
    if (!isSyncingData) {
      setIsDirty(true);
    }
    
    // Clear error when field is updated
    if (errors[name]) {
      setErrors(prev => {
        const newErrors = { ...prev };
        delete newErrors[name];
        return newErrors;
      });
    }
  };

  // Helper to find customer by ID (handles both string and numeric IDs)
  const findCustomerById = useCallback((customerId: string): Customer | undefined => {
    return customers.find((c: Customer) => c.id === customerId || c.id === customerId.toString());
  }, [customers]);

  // Helper to get customer by ID from form data (handles both string and numeric IDs)
  const getCustomerFromFormData = useCallback((): Customer | undefined => {
    if (!formData.customer_id) return undefined;
    
    // Use real-time customer data if available
    if (realTimeCustomer && realTimeCustomer.id === formData.customer_id) {
      return realTimeCustomer;
    }
    
    // Try to find customer in the customer store (which includes both local and external customers)
    let customer = customers.find((c: Customer) => {
      const customerId = c.id?.toString();
      const formCustomerId = formData.customer_id?.toString();
      return customerId === formCustomerId;
    });
    
    // If not found by ID, try by email
    if (!customer && formData.customer_email) {
      customer = customers.find((c: Customer) => c.email === formData.customer_email);
    }
    
    // If still not found and we have a project, try the project's customer_id
    if (!customer && project?.customer_id) {
      customer = customers.find((c: Customer) => {
        const customerId = c.id?.toString();
        const projectCustomerId = project.customer_id?.toString();
        return customerId === projectCustomerId;
      });
    }
    
    // If still not found and we have a project, try the project's customer_email
    if (!customer && project?.customer_email) {
      customer = customers.find((c: Customer) => c.email === project.customer_email);
    }
    
    return customer;
  }, [customers, formData.customer_id, formData.customer_email, project?.customer_id, project?.customer_email, realTimeCustomer]);

  // Helper kept for backward compat (no longer used): customer IDs are UUID strings
  const getCustomerIdAsNumber = useCallback((): number | undefined => {
    return undefined;
  }, []);

  // Helper to validate form data
  const validateFormData = useCallback((data: ProjectFormData): Record<string, string> => {
    const validationErrors: Record<string, string> = {};
    
    if (!data.project_name?.trim()) {
      validationErrors.project_name = 'Project name is required';
    }
    
    if (!data.customer_id) {
      validationErrors.customer_id = 'Customer is required';
    } else {
      const customer = findCustomerById(data.customer_id);
      if (!customer) {
        validationErrors.customer_id = 'Selected customer not found';
      }
    }
    
    return validationErrors;
  }, [findCustomerById]);

  // Validate form
  const validateForm = useCallback((): boolean => {
    const validationErrors = validateFormData(formData);
    setErrors(validationErrors);
    return Object.keys(validationErrors).length === 0;
  }, [formData, validateFormData]);

  // Helper to convert form data to project data
  const convertFormDataToProject = useCallback((data: ProjectFormData): Omit<Project, 'id' | 'created_at' | 'updated_at'> => {
    return {
      project_name: data.project_name,
      customer_id: data.customer_id,
      customer_name: data.customer_name || '',
      customer_email: data.customer_email || '',
      status: 'pending' as const,
      notes: data.notes,
      deposit_received: data.deposit_received,
      ordered: data.ordered,
      delivery_date: data.delivery_date || null,
      install_date: data.install_date || null,
      product_types: data.product_types,
      supplier_quote_received: data.supplier_quote_received,
      supplier_prices: data.supplier_prices,
      complete: data.complete,
      project_status: data.project_status || 'active',
      workflow_tasks: data.workflow_tasks,
      preferred_suppliers: data.preferred_suppliers || [],
      onedrive_folder_id: null,
      supply_only: data.supply_only
    };
  }, []);

  const getLifecycleTimestampPatch = (
    previousStatus: 'active' | 'completed' | 'cancelled' | null,
    nextStatus: 'active' | 'completed' | 'cancelled'
  ): { status_changed_at?: string; completed_at?: string | null; cancelled_at?: string | null } => {
    const nowIso = new Date().toISOString();
    const statusChanged = previousStatus == null || previousStatus !== nextStatus;
    const patch: { status_changed_at?: string; completed_at?: string | null; cancelled_at?: string | null } = {};
    if (statusChanged) {
      patch.status_changed_at = nowIso;
    }
    if (nextStatus === 'completed') {
      patch.completed_at = statusChanged || !project?.completed_at ? nowIso : project.completed_at;
      patch.cancelled_at = null;
      return patch;
    }
    if (nextStatus === 'cancelled') {
      patch.cancelled_at = statusChanged || !project?.cancelled_at ? nowIso : project.cancelled_at;
      patch.completed_at = null;
      return patch;
    }
    patch.completed_at = null;
    patch.cancelled_at = null;
    return patch;
  };

  // Modify handleTasksChange to setFormData with the new workflow_tasks array
  const handleTasksChange = useCallback((tasks: WorkflowTask[]) => {
    const normalizeTasks = (taskList: WorkflowTask[]) => {
      return taskList.map(task => ({
        task: task.task,
        completed: task.completed,
        date: task.date || undefined,
        productionDates: task.productionDates || undefined
      })).sort((a, b) => a.task.localeCompare(b.task));
    };

    const normalizedNew = normalizeTasks(tasks);
    setWorkflowTasks(tasks);
    setFormData(prev => ({
      ...prev,
      workflow_tasks: tasks
    }));
    
    // Always set isDirty for workflow task changes (including production dates)
    // These are user-initiated changes that should always be tracked
    setIsDirty(true);
  }, []);

  // Modify handleStageChange to not set isDirty
  const handleStageChange = async (stage: string) => {
    try {
      if (!projectId || isSyncingData) return;
      
      const updates = {
        workflow_tasks: workflowTasks,
        complete: stage === 'Completed',
        ordered: stage === 'Ordered' || stage === 'Pending Installation' || stage === 'Completed',
        deposit_received: stage === 'Deposit Received' || stage === 'Ordered' || stage === 'Pending Installation' || stage === 'Completed',
        supplier_quote_received: stage === 'Supplier Quote Received' || stage === 'Deposit Received' || stage === 'Ordered' || stage === 'Pending Installation' || stage === 'Completed'
      };
      
      setFormData(prev => ({
        ...prev,
        ...updates
      }));

      return Promise.resolve();
    } catch (err) {
      console.error('Stage change error:', err);
      throw new Error(err instanceof Error ? err.message : 'Unknown error occurred');
    }
  };

  // Update the save function
  const handleSave = async () => {
    if (isSaving) {
      return;
    }

    try {
      setIsSaving(true);
      setLastSaveError(null);

      // Validate required fields
      if (!formData.project_name || !formData.customer_name) {
        throw new Error('Project name and customer name are required');
      }

      // Get the current customer data - prioritize real-time data over cached data
      const currentCustomer = realTimeCustomer || customers.find(c => c.id === formData.customer_id);

      // Create a snapshot of the current state
      const fullWorkflowTasks = getFullWorkflowTasks(workflowTasks, formData);
      // New workspaces take the customer's brand (Admin → Organisations)
      const newBrandId = projectId
        ? null
        : (brandForCustomer(await organisationSettings.list().catch(() => []), currentCustomer as any)?.id || null);
      const lifecycle_ref = projectId
        ? (project?.lifecycle_ref || null)
        : await mintWorkspaceLifecycleRef(newBrandId);
      const projectData: Omit<Project, 'id' | 'created_at' | 'updated_at'> = {
        project_name: formData.project_name,
        customer_id: formData.customer_id,
        customer_name: currentCustomer?.name || formData.customer_name || '',
        customer_email: currentCustomer?.email || formData.customer_email || '',
        notes: formData.notes,
        deposit_received: formData.deposit_received,
        ordered: formData.ordered,
        delivery_date: formData.delivery_date || null,
        product_types: formData.product_types,
        supplier_quote_received: formData.supplier_quote_received,
        supplier_prices: supplierPrices,
        complete: formData.complete,
        workflow_tasks: fullWorkflowTasks,
        status: 'pending' as const,
        preferred_suppliers: formData.preferred_suppliers || [],
        onedrive_folder_id: project?.onedrive_folder_id || null,
        supply_only: formData.supply_only,
        lifecycle_ref,
        ...(!projectId
          ? { use_billing_details_for_documents: defaultUseBillingDetailsForDocuments(currentCustomer) }
          : {}),
      };
      const nextLifecycleStatus = (formData.project_status || 'active') as 'active' | 'completed' | 'cancelled';
      const previousLifecycleStatus = projectId
        ? ((project?.project_status || 'active') as 'active' | 'completed' | 'cancelled')
        : null;
      const lifecycleTimestampPatch = getLifecycleTimestampPatch(previousLifecycleStatus, nextLifecycleStatus);
      const projectDataWithLifecycle = {
        ...projectData,
        ...lifecycleTimestampPatch
      };

      if (projectId && nextLifecycleStatus === 'completed' && previousLifecycleStatus !== 'completed') {
        await assertJobRecordAllowsComplete(projectId);
      }

      if (projectId) {
        // Update existing project
        const previousCustomerId = project?.customer_id;
        await updateProject(projectDataWithLifecycle as Partial<Project>);
        if (
          formData.customer_id != null
          && String(previousCustomerId || '') !== String(formData.customer_id)
        ) {
          const { emitProjectCustomerChangedActivity } = await import('../services/activityEvents.service');
          emitProjectCustomerChangedActivity({
            projectId,
            previousCustomerId,
            customerId: formData.customer_id,
            customerName: currentCustomer?.name || formData.customer_name,
          });
        }
        await refetch();
      } else {
        // Create new project
        const insertPayload = await withProjectCreator(projectDataWithLifecycle as Record<string, unknown>);
        const { data, error } = await directSupabase
          .from('projects')
          .insert([insertPayload])
          .select()
          .single();
        
        if (error) {
          throw new Error(error.message);
        }
        
        if (data) {
          if (newBrandId) await saveWorkspaceBrand(data.id, newBrandId);
          emitProjectCreatedActivity({
            projectId: data.id,
            customerId: data.customer_id,
            customerName: data.customer_name,
            actorId: (insertPayload as { created_by?: string }).created_by || null,
          });
          const convertedFormData = convertProjectToFormData(data);
          setFormData(convertedFormData);
          setSupplierPrices(Array.isArray(data.supplier_prices) ? data.supplier_prices as unknown as any[] : []);
          setWorkflowTasks(Array.isArray(data.workflow_tasks) ? data.workflow_tasks as unknown as WorkflowTask[] : getInitialWorkflowTasks());
          navigate(`/projects/${data.id}`);
        } else {
          throw new Error('Failed to create project');
        }
      }

      setIsDirty(false);
      setLastSaved(new Date());
    } catch (error) {
      console.error('Error saving project:', error);
      setLastSaveError(error instanceof Error ? error.message : 'Failed to save project');
    } finally {
      setIsSaving(false);
    }
  };

  // Handle form submission
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setSubmitError(null);
    setIsSubmitting(true);

    try {
      // Validate form
      const validationErrors: Record<string, string> = {};
      if (!formData.project_name) {
        validationErrors.project_name = 'Project name is required';
      }
      if (!formData.customer_id) {
        validationErrors.customer_id = 'Customer is required';
      }

      if (Object.keys(validationErrors).length > 0) {
        setErrors(validationErrors);
        setIsSubmitting(false);
        return;
      }

      // Get the current customer data - prioritize real-time data over cached data
      const currentCustomer = realTimeCustomer || customers.find(c => c.id === formData.customer_id);
      // New workspaces take the customer's brand (Admin → Organisations)
      const newBrandId = projectId
        ? null
        : (brandForCustomer(await organisationSettings.list().catch(() => []), currentCustomer as any)?.id || null);

      // Prepare project data
      const projectData: Omit<Project, 'id' | 'created_at' | 'updated_at'> = {
        ...formData,
        workflow_tasks: workflowTasks,
        customer_id: formData.customer_id,
        customer_name: currentCustomer?.name || formData.customer_name || project?.customer_name || '',
        customer_email: currentCustomer?.email || formData.customer_email || project?.customer_email || '',
        supplier_prices: supplierPrices,
        status: 'pending' as const,
        onedrive_folder_id: project?.onedrive_folder_id || null,
        delivery_date: formData.delivery_date || null,
        supply_only: formData.supply_only,
        preferred_suppliers: formData.preferred_suppliers || []
      };
      const nextLifecycleStatus = (formData.project_status || 'active') as 'active' | 'completed' | 'cancelled';
      const previousLifecycleStatus = projectId
        ? ((project?.project_status || 'active') as 'active' | 'completed' | 'cancelled')
        : null;
      const lifecycleTimestampPatch = getLifecycleTimestampPatch(previousLifecycleStatus, nextLifecycleStatus);
      const projectDataWithLifecycle = {
        ...projectData,
        ...lifecycleTimestampPatch
      };

      if (projectId && nextLifecycleStatus === 'completed' && previousLifecycleStatus !== 'completed') {
        await assertJobRecordAllowsComplete(projectId);
      }

      if (projectId) {
        // Update existing project
        try {
          const previousCustomerId = project?.customer_id;
          const { error } = await directSupabase
            .from('projects')
            .update(projectDataWithLifecycle)
            .eq('id', projectId);
          
          if (error) {
            throw new Error(error.message);
          }
          if (
            formData.customer_id != null
            && String(previousCustomerId || '') !== String(formData.customer_id)
          ) {
            const { emitProjectCustomerChangedActivity } = await import('../services/activityEvents.service');
            emitProjectCustomerChangedActivity({
              projectId,
              previousCustomerId,
              customerId: formData.customer_id,
              customerName: currentCustomer?.name || formData.customer_name,
            });
          }
        } catch (error) {
          throw new Error(error instanceof Error ? error.message : 'Failed to update project');
        }

        await refetch();
        setIsDirty(false);
        // Set flag to bypass navigation guards since we just saved
        isNavigatingAfterSaveRef.current = true;
        
        // Navigate back to project details - bypass navigation guard since we just saved
        // Use window.location to force navigation and bypass any React Router issues
        window.location.href = `/projects/${projectId}`;
      } else {
        // Create new project
        const insertPayload = await withProjectCreator({
          ...projectDataWithLifecycle,
          lifecycle_ref: String(projectDataWithLifecycle.lifecycle_ref || '').trim() || await mintWorkspaceLifecycleRef(newBrandId),
          use_billing_details_for_documents: defaultUseBillingDetailsForDocuments(currentCustomer),
        } as Record<string, unknown>);
        const { data, error } = await directSupabase
          .from('projects')
          .insert([insertPayload])
          .select()
          .single();
        
        if (error) {
          throw new Error(error.message);
        }
        
        if (data) {
          if (newBrandId) await saveWorkspaceBrand(data.id, newBrandId);
          emitProjectCreatedActivity({
            projectId: data.id,
            customerId: data.customer_id,
            customerName: data.customer_name,
            actorId: (insertPayload as { created_by?: string }).created_by || null,
          });
          setIsDirty(false);
          navigate(`/projects/${data.id}`);
        } else {
          throw new Error('Failed to create project');
        }
      }
    } catch (err) {
      console.error('Error saving project:', err);
      setSubmitError(err instanceof Error ? err.message : 'Failed to save project');
    } finally {
      setIsSubmitting(false);
    }
  };

  const ensureProjectSaved = async (): Promise<boolean> => {
    if (!isDirty) return true;
    
    try {
      await handleSubmit(new Event('submit') as any);
      return true;
    } catch (err) {
      console.error('Error saving project:', err);
      return false;
    }
  };

  // Replace handleSendFirstSurveyAppointmentRequest
  const handleSendFirstSurveyAppointmentRequest = async () => {
    if (!projectId || !project || !project.id) {
      alert('Project must be saved before sending the appointment request email.');
      return;
    }
    const customer = findCustomerById(project.customer_id?.toString() || '');
    if (!customer?.email) {
      alert('Customer email not found. Please ensure the customer has a valid email address.');
      return;
    }
    try {
      await sendTemplateEmail({
        templateType: 'firstSurveyAppointmentRequest',
        project,
        customer: convertCustomerForEmail(customer)
      });
      alert('Appointment request email sent to ' + customer.email);
    } catch (err: unknown) {
      const errorMessage = err instanceof Error 
        ? err.message 
        : typeof err === 'string' 
          ? err 
          : 'An unknown error occurred';
      alert('Failed to send email: ' + errorMessage);
    }
  };

  // Replace handleSendFinalSurveyAppointmentRequest
  const handleSendFinalSurveyAppointmentRequest = async () => {
    if (!projectId || !project || !project.id) {
      alert('Project must be saved before sending the final survey appointment request email.');
      return;
    }
    const customer = findCustomerById(project.customer_id?.toString() || '');
    if (!customer?.email) {
      alert('Customer email not found. Please ensure the customer has a valid email address.');
      return;
    }
    try {
      await sendTemplateEmail({
        templateType: 'finalSurveyAppointmentRequest',
        project,
        customer: convertCustomerForEmail(customer)
      });
      alert('Final survey appointment request email sent to ' + customer.email);
    } catch (err: unknown) {
      const errorMessage = err instanceof Error 
        ? err.message 
        : typeof err === 'string' 
          ? err 
          : 'An unknown error occurred';
      alert('Failed to send email: ' + errorMessage);
    }
  };

  // Update the customer info display section
  const renderCustomerInfo = () => {
    const customer = getCustomerFromFormData() || missingCustomer;
    
    // Show loading state when doing real-time lookup
    if (customerLookupLoading) {
      return (
        <Stack spacing={2}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <CircularProgress size="sm" />
            <Typography level="body-sm">Loading customer data...</Typography>
          </Box>
        </Stack>
      );
    }
    
    // If customer is not found but we have project data, show a warning
    if (!customer && project?.customer_id && customers.length > 0) {
      // Customer may be loading from external API or not found in current list
    }
    
    return (
      <Stack spacing={2}>
        <FormControl>
          <FormLabel>Name</FormLabel>
          <Input value={customer?.name || project?.customer_name || ''} readOnly />
        </FormControl>
        <FormControl>
          <FormLabel>Phone Number</FormLabel>
          <Input value={customer?.phone || ''} readOnly />
        </FormControl>
        <FormControl>
          <FormLabel>Email</FormLabel>
          <Input value={customer?.email || project?.customer_email || ''} readOnly />
        </FormControl>
        <FormControl>
          <FormLabel>Address</FormLabel>
          <Textarea value={customer?.address || ''} readOnly minRows={2} />
        </FormControl>
        <FormControl>
          <FormLabel>Billing Address</FormLabel>
          <Textarea value={customer?.notes || ''} readOnly minRows={2} />
        </FormControl>
        <FormControl>
          <FormLabel>Organization Name</FormLabel>
          <Input value={customer?.company || ''} readOnly />
        </FormControl>
        <FormControl>
          <FormLabel>Created At</FormLabel>
          <Input value={customer?.created_at || ''} readOnly />
        </FormControl>
        <FormControl>
          <FormLabel>Updated At</FormLabel>
          <Input value={customer?.updated_at || ''} readOnly />
        </FormControl>
      </Stack>
    );
  };

  // Update the customer selection section in the form
  const renderCustomerSelection = () => {
    // Include missing customer in options if it exists
    const allCustomers = missingCustomer && !customers.find(c => c.id === missingCustomer.id) 
      ? [...customers, missingCustomer] 
      : customers;
      
    // Include real-time customer in options if it exists and is different from cached data
    const finalCustomers = realTimeCustomer && !allCustomers.find(c => c.id === realTimeCustomer.id)
      ? [...allCustomers, realTimeCustomer]
      : allCustomers;
      
    return (
      <Stack spacing={2}>
        <FormControl error={!!errors.customer_id} required>
          <FormLabel>Customer</FormLabel>
          <Autocomplete<Customer>
            placeholder="Search for a customer..."
            options={finalCustomers}
            getOptionLabel={(option) => `${option.name} (${option.phone || 'No phone'})`}
            value={finalCustomers.find(c => c.id === formData.customer_id) || null}
            onChange={(_, newValue) => handleCustomerSelect(newValue)}
            error={!!errors.customer_id}
            isOptionEqualToValue={(option, value) => option.id === value.id}
            renderOption={(props, option) => {
              const { key, ownerState, ...otherProps } = props as any;
              const isRealTime = realTimeCustomer && option.id === realTimeCustomer.id;
              return (
                <Box 
                  component="li" 
                  {...otherProps}
                  key={key}
                  sx={{
                    width: '100%',
                    boxSizing: 'border-box',
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'flex-start',
                    pt: 0.2,
                    pb: 1.25,
                    px: 2,
                    borderBottom: '1px solid',
                    borderColor: 'divider',
                    gap: 0.5,
                    '&:last-child': { borderBottom: 'none' },
                    cursor: 'pointer',
                    minHeight: 52,
                    backgroundColor: 'background.surface',
                    '&:hover': {
                      backgroundColor: 'primary.softBg',
                    }
                  }}
                >
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, width: '100%' }}>
                    <Typography fontWeight="lg" sx={{ fontSize: '1rem', mt: 0 }}>{option.name}</Typography>
                    {isRealTime && (
                      <Chip size="sm" color="primary" variant="soft">
                        Real-time
                      </Chip>
                    )}
                  </Box>
                  <Typography level="body-xs" sx={{ color: 'neutral.500', lineHeight: 1.5, mb: 0 }}>
                    {option.phone || 'No phone'} • {option.email || 'No email'}
                  </Typography>
                </Box>
              );
            }}
          />
          {errors.customer_id && (
            <FormHelperText>{errors.customer_id}</FormHelperText>
          )}
          {realTimeCustomer && (
            <FormHelperText color="primary">
              Using real-time customer data from external API
            </FormHelperText>
          )}
        </FormControl>
      </Stack>
    );
  };

  // Handle project editing
  const handleEditProject = () => {
    navigate(`/projects/${projectId}/edit`);
  };



  // Handler for supply only toggle
  const handleSupplyOnlyChange = (supplyOnly: boolean) => {
    setFormData(prev => ({
      ...prev,
      supply_only: supplyOnly,
    }));
    setWorkflowTasks(prevTasks => getFullWorkflowTasksForMode(prevTasks, supplyOnly, formData));
    setIsDirty(true);
  };

  // Load customers on mount
  useEffect(() => {
    loadCustomers();
  }, []); // Empty dependency array to run only on mount

  // Real-time customer lookup when customers are loaded but customer not found
  useEffect(() => {
    const performRealTimeLookup = async () => {
      // Only run if we have customers loaded but no customer found
      if (customers.length > 0 && project && !getCustomerFromFormData()) {
        try {
          // Use customer store to load fresh data (includes both local and external)
          await loadCustomers();
        } catch (error) {
          console.error('Real-time customer lookup failed:', error);
        }
      }
    };

    // Run after a short delay to ensure initial data loading is complete
    const timeoutId = setTimeout(performRealTimeLookup, 1000);
    return () => clearTimeout(timeoutId);
  }, [customers.length, project, getCustomerFromFormData, loadCustomers]);



  // Remove debug log
  const normalizedWorkflowTasks = getFullWorkflowTasksForMode(workflowTasks, formData.supply_only, formData);

  // Define the customer variable for EventManager section
  const customer = getCustomerFromFormData() || missingCustomer;



  // Handle loading states
  if (projectLoading || customersLoading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
        <CircularProgress size="lg" />
      </Box>
    );
  }

  // Handle error states
  if (projectError) {
    return (
      <Box sx={{ p: 2 }}>
        <Alert color="danger">
          {projectError}
        </Alert>
        <Button
          startDecorator={<IconWrapper icon={FaArrowLeft} />}
          onClick={() => navigate('/projects')}
          sx={{ mt: 2 }}
        >
          Back to Projects
        </Button>
      </Box>
    );
  }

  // Ensure the main return is at the top level of the component
  return (
    <Box sx={{ px: { xs: 1, md: 2 }, width: '100%', maxWidth: '100vw', boxSizing: 'border-box' }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
          <Button
            variant="plain"
            onClick={() => handleNavigationAttempt(projectId ? `/projects/${projectId}` : '/projects')}
            startDecorator={<IconWrapper icon={FaArrowLeft} />}
          >
            {projectId ? 'Back to Project' : 'Back to Projects'}
          </Button>
          <Typography level="h2">{projectId ? 'Edit Project' : 'New Project'}</Typography>
        </Box>
      </Box>
      
      {submitError && (
        <Alert color="danger" sx={{ mb: 3 }}>
          {submitError}
        </Alert>
      )}
      
      {/* Navigation Guard Modal */}
      <Modal open={showNavigationModal} onClose={handleNavigationCancel}>
        <ModalDialog>
          <DialogTitle>Unsaved Changes</DialogTitle>
          <DialogContent>
            <Typography>
              You have unsaved changes to this project. Are you sure you want to leave without saving?
            </Typography>
          </DialogContent>
          <DialogActions>
            <Button 
              variant="outlined" 
              color="neutral" 
              onClick={handleNavigationCancel}
            >
              Stay on Page
            </Button>
            <Button 
              color="danger" 
              onClick={handleNavigationConfirm}
            >
              Leave Without Saving
            </Button>
          </DialogActions>
        </ModalDialog>
      </Modal>
      
      <Grid container spacing={2}>
        {/* Left: Project Information */}
        <Grid xs={12} md={6}>
          <Card sx={{ p: 3, position: 'relative' }}>
            {isSubmitting && (
              <Box
                sx={{
                  position: 'absolute',
                  top: 0,
                  left: 0,
                  right: 0,
                  bottom: 0,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  backgroundColor: 'rgba(255, 255, 255, 0.7)',
                  zIndex: 1,
                }}
              >
                <CircularProgress />
              </Box>
            )}
            <form 
              onSubmit={(e) => {
                handleSubmit(e);
              }}
            >
              <Stack spacing={2}>
                <Typography level="title-lg">Project Information</Typography>
                <Divider />
                {/* Customer Information Section */}
                <Box>
                  <Typography level="title-md" sx={{ mb: 1 }}>Customer Information</Typography>
                  <Stack spacing={2}>
                    {renderCustomerSelection()}
                    {renderCustomerInfo()}
                  </Stack>
                </Box>
                <Divider />
                {/* Project Details Section */}
                <Box>
                  <Typography level="title-md" sx={{ mb: 1 }}>Project Details</Typography>
                  <Stack spacing={2}>
                    <FormControl error={!!errors.project_name} required>
                      <FormLabel>Project Name</FormLabel>
                      <Input
                        name="project_name"
                        value={formData.project_name}
                        onChange={handleChange}
                        placeholder="Enter project name"
                        error={!!errors.project_name}
                      />
                      {errors.project_name && (
                        <Typography level="body-xs" color="danger">
                          {errors.project_name}
                        </Typography>
                      )}
                    </FormControl>

                    {/* Grouped Status Fields */}
                    <Box>
                      <Typography level="title-md" sx={{ mb: 1 }}>Project Status</Typography>
                      <Stack spacing={2}>
                        <FormControl>
                          <FormLabel>Progress Stage</FormLabel>
                          {(() => {
                            const statusWorkflowStage = getStageFromTasks(formData.workflow_tasks || [], formData) as WorkflowStage;
                            const colorMap = {
                              'New': 'neutral',
                              'Supplier Quote Received': 'primary',
                              'Deposit Received': 'warning',
                              'Ordered': 'danger',
                              'Installation Scheduled': 'warning',
                              'Completed': 'success',
                            };
                            return (
                              <Chip size="md" color={colorMap[statusWorkflowStage as keyof typeof colorMap] as ColorPaletteProp} variant="soft">
                                {statusWorkflowStage}
                              </Chip>
                            );
                          })()}
                          <FormHelperText>This is determined by the workflow below</FormHelperText>
                        </FormControl>
                      </Stack>
                    </Box>

                    <FormControl>
                      <FormLabel>Notes</FormLabel>
                      <Textarea
                        name="notes"
                        value={formData.notes}
                        onChange={handleChange}
                        minRows={2}
                        placeholder="Any additional notes"
                      />
                    </FormControl>

                    {/* Add multi-select field for product types */}
                    <FormControl>
                      <FormLabel>Product Types</FormLabel>
                      <Select
                        multiple
                        value={formData.product_types || []}
                        onChange={(_, newValue) => {
                          setFormData(prev => ({
                            ...prev,
                            product_types: Array.isArray(newValue)
                              ? newValue.map(v =>
                                  typeof v === 'string'
                                    ? v
                                    : (typeof v === 'object' && v !== null && 'value' in v)
                                      ? String((v as any).value)
                                      : String(v)
                                )
                              : []
                          }));
                        }}
                        placeholder="Select product types..."
                        renderValue={(selected) => (
                          <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap' }}>
                            {selected.map((value, idx) => {
                              const label = typeof value === 'string'
                                ? value
                                : (typeof value === 'object' && value !== null && 'value' in value)
                                  ? String((value as any).value)
                                  : String(value);
                              return (
                                <Chip
                                  key={label + idx}
                                  size="sm"
                                  variant="soft"
                                  color="primary"
                                >
                                  {label}
                                </Chip>
                              );
                            })}
                          </Box>
                        )}
                        slotProps={{
                          listbox: {
                            sx: { maxHeight: '200px', overflow: 'auto' }
                          }
                        }}
                      >
                        {PRODUCT_TYPE_OPTIONS.map((type) => (
                          <Option key={type} value={type}>{type}</Option>
                        ))}
                      </Select>
                      <FormHelperText>You can select multiple product types</FormHelperText>
                    </FormControl>

                    {/* Suppliers & Prices Section */}
                    <Box>
                      <Typography level="title-md" sx={{ mb: 1 }}>Suppliers & Prices</Typography>
                      <Stack spacing={2}>
                        <FormControl>
                          <FormLabel>Preferred Suppliers</FormLabel>
                          <Select
                            multiple
                            value={preferredSuppliers}
                            onChange={(_, newValue) => {
                              setPreferredSuppliers(Array.isArray(newValue) ? newValue : []);
                              if (!isSyncingData) {
                                setIsDirty(true);
                              }
                            }}
                            placeholder="Select preferred suppliers..."
                            renderValue={selected => (
                              <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap' }}>
                                {(Array.isArray(selected) ? selected : []).map((id, idx) => {
                                  const supplierId = typeof id === 'string' ? id : (id && typeof id === 'object' && 'value' in id ? String(id.value) : String(id));
                                  const supplier = allSuppliers.find(s => s.id === supplierId);
                                  return (
                                    <Chip key={supplierId} size="sm" variant="soft" color="primary">
                                      {supplier?.supplier_name || supplierId}
                                    </Chip>
                                  );
                                })}
                              </Box>
                            )}
                            slotProps={{
                              listbox: { sx: { maxHeight: '200px', overflow: 'auto' } }
                            }}
                          >
                            {allSuppliers.map(supplier => (
                              <Option key={supplier.id} value={supplier.id}>{supplier.supplier_name}</Option>
                            ))}
                          </Select>
                          <FormHelperText>Select one or more preferred suppliers for this project</FormHelperText>
                        </FormControl>
                        <FormControl>
                          <FormLabel>Suppliers</FormLabel>
                          <Select
                            multiple
                            value={supplierPrices.map(sp => sp.supplier_id)}
                            onChange={(_, newValue) => {
                              setSupplierPrices(prev => {
                                const newIds = Array.isArray(newValue) ? newValue : [];
                                let updated = prev.filter(sp => newIds.includes(sp.supplier_id));
                                newIds.forEach(id => {
                                  if (!updated.find(sp => sp.supplier_id === id)) {
                                    const supplier = allSuppliers.find(s => s.id === id);
                                    updated.push({ 
                                      supplier_id: id, 
                                      supplier_name: supplier?.supplier_name || '', 
                                      price: '',
                                      supplier_price_date_obtained: '',
                                      quote_reference: ''
                                    });
                                  }
                                });
                                return updated;
                              });
                              if (!isSyncingData) {
                                setIsDirty(true);
                              }
                            }}
                            placeholder="Select suppliers..."
                            renderValue={selected => (
                              <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap' }}>
                                {(Array.isArray(selected) ? selected : []).map((id, idx) => {
                                  const supplierId = typeof id === 'string' ? id : (id && typeof id === 'object' && 'value' in id ? String(id.value) : String(id));
                                  const supplier = allSuppliers.find(s => s.id === supplierId);
                                  return (
                                    <Chip key={supplierId} size="sm" variant="soft" color="primary">
                                      {supplier?.supplier_name || supplierId}
                                    </Chip>
                                  );
                                })}
                              </Box>
                            )}
                            slotProps={{
                              listbox: { sx: { maxHeight: '200px', overflow: 'auto' } }
                            }}
                          >
                            {allSuppliers.map(supplier => (
                              <Option key={supplier.id} value={supplier.id}>{supplier.supplier_name}</Option>
                            ))}
                          </Select>
                          <FormHelperText>Select suppliers and enter a price for each, and the date the quote was obtained</FormHelperText>
                        </FormControl>

                        {/* Supplier Prices */}
                        <Stack spacing={1}>
                          {supplierPrices.map((sp, idx) => (
                            <Box key={sp.supplier_id} sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                              <Typography level="body-sm" sx={{ minWidth: 120 }}>{sp.supplier_name}</Typography>
                              <Input
                                placeholder="Quote Reference"
                                value={sp.quote_reference || ''}
                                onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                                  const val = e.target.value;
                                  setSupplierPrices(prev => prev.map((item, i) => 
                                    i === idx ? { ...item, quote_reference: val } : item
                                  ));
                                  if (!isSyncingData) {
                                    setIsDirty(true);
                                  }
                                }}
                                sx={{ width: 140 }}
                              />
                              <Input
                                type="number"
                                placeholder="Price"
                                value={sp.price == null ? '' : sp.price}
                                onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                                  const val = e.target.value;
                                  setSupplierPrices(prev => prev.map((item, i) => 
                                    i === idx ? { ...item, price: val } : item
                                  ));
                                  if (!isSyncingData) {
                                    setIsDirty(true);
                                  }
                                }}
                                sx={{ width: 120 }}
                              />
                              <Input
                                type="date"
                                placeholder="Date obtained"
                                value={sp.supplier_price_date_obtained || ''}
                                slotProps={{ 
                                  input: { 
                                    max: new Date().toISOString().split('T')[0]
                                  } 
                                }}
                                onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                                  const val = e.target.value;
                                  if (validateSupplierPriceDate(val)) {
                                    setSupplierPrices(prev => prev.map((item, i) => 
                                      i === idx ? { ...item, supplier_price_date_obtained: val } : item
                                    ));
                                    if (!isSyncingData) {
                                      setIsDirty(true);
                                    }
                                  } else {
                                    alert('Date cannot be in the future');
                                  }
                                }}
                                error={!!(sp.supplier_price_date_obtained && !validateSupplierPriceDate(sp.supplier_price_date_obtained))}
                                sx={{ width: 160 }}
                              />
                              {sp.supplier_price_date_obtained && !validateSupplierPriceDate(sp.supplier_price_date_obtained) && (
                                <Typography level="body-xs" color="danger">
                                  Date cannot be in the future
                                </Typography>
                              )}
                            </Box>
                          ))}
                        </Stack>
                      </Stack>
                    </Box>
                  </Stack>
                </Box>
                <Box sx={{ mt: 4, display: 'flex', gap: 1, justifyContent: 'flex-end' }}>
                  <Button
                    variant="outlined"
                    color="neutral"
                    onClick={() => handleNavigationAttempt(projectId ? `/projects/${projectId}` : '/projects')}
                  >
                    Cancel
                  </Button>
                  <Button
                    onClick={(e: React.MouseEvent) => {
                      e.preventDefault();
                      handleSubmit(e as any);
                    }}
                    disabled={isSubmitting}
                  >
                    {projectId ? 'Update Project' : 'Create Project'}
                  </Button>
                  {projectId && (
                    <Button
                      startDecorator={<IconWrapper icon={FaTrash} />}
                      color="danger"
                      variant="outlined"
                      onClick={() => setDeleteDialogOpen(true)}
                    >
                      Delete Project
                    </Button>
                  )}
                </Box>
              </Stack>
            </form>
          </Card>
        </Grid>
        {/* Right: Project Workflow */}
        <Grid xs={12} md={6}>
          <ProjectWorkflow 
            workflowTasks={normalizedWorkflowTasks}
            onTasksChange={handleTasksChange}
            onTaskChange={(taskName, completed) => {
              // Use normalizedWorkflowTasks as the source instead of workflowTasks
              // because normalizedWorkflowTasks includes all tasks from getFullWorkflowTasksForMode
              let updatedTasks = normalizedWorkflowTasks.map(task => 
                task.task === taskName ? { ...task, completed } : task
              );
              
              // Mutual exclusivity: If "Cancel Project" is checked, uncheck "Complete Project"
              if (taskName === 'Cancel Project' && completed) {
                updatedTasks = updatedTasks.map(task => 
                  task.task === 'Complete Project' ? { ...task, completed: false } : task
                );
                setFormData(prev => ({ ...prev, project_status: 'cancelled', complete: false }));
              }
              // If "Cancel Project" is unchecked, set project_status back to 'active'
              if (taskName === 'Cancel Project' && !completed) {
                setFormData(prev => ({ ...prev, project_status: 'active' }));
              }
              
              // Mutual exclusivity: If "Complete Project" is checked, uncheck "Cancel Project"
              if (taskName === 'Complete Project' && completed) {
                updatedTasks = updatedTasks.map(task => 
                  task.task === 'Cancel Project' ? { ...task, completed: false } : task
                );
                setFormData(prev => ({ ...prev, project_status: 'completed', complete: true }));
              }
              // If "Complete Project" is unchecked, set project_status back to 'active'
              if (taskName === 'Complete Project' && !completed) {
                setFormData(prev => ({ ...prev, project_status: 'active', complete: false }));
              }
              
              handleTasksChange(updatedTasks);
            }}
            condensed={false}
            project={formData}
            onSendFirstSurveyAppointmentRequest={handleSendFirstSurveyAppointmentRequest}
            onSendFinalSurveyAppointmentRequest={handleSendFinalSurveyAppointmentRequest}
            onProjectUpdated={refetch}
            onEnsureProjectSaved={ensureProjectSaved}
            onProjectCompleteChange={(completed) => {
              setFormData(prev => ({ ...prev, complete: completed }));
              if (!isSyncingData) {
                setIsDirty(true);
              }
            }}
            readOnly={false}
            supplyOnly={formData.supply_only}
            onSupplyOnlyChange={handleSupplyOnlyChange}
          />
        </Grid>
      </Grid>
      
      {/* Delete confirmation modal */}
      {projectId && (
        <Modal open={deleteDialogOpen} onClose={() => setDeleteDialogOpen(false)}>
          <ModalDialog>
            <Typography level="h4" mb={2}>Confirm Delete</Typography>
            <Typography mb={3}>
              This removes the entire job: First and Final Surveys, quote versions, customer transmissions and share links, deltas, activity history, and associated workspace records. Nothing from this lifecycle is left behind.
            </Typography>
            <Box sx={{ display: 'flex', gap: 1, justifyContent: 'flex-end' }}>
              <Button variant="outlined" color="neutral" onClick={() => setDeleteDialogOpen(false)}>Cancel</Button>
              <Button color="danger" onClick={async () => {
                if (projectId) {
                  await deleteProject(projectId);
                  setDeleteDialogOpen(false);
                  navigate('/projects');
                }
              }}>Delete</Button>
            </Box>
          </ModalDialog>
        </Modal>
      )}
    </Box>
  );
};

export default ProjectFormPage; 